import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { KubernetesConsoleApi } from './api';
import { COLORS } from './KubernetesPage';

interface TourStep {
  id: string;
  selector: string;
  title: string;
  body: string;
}

// Spotlights real elements already on the page — see the matching
// `data-tour-id` attributes in KubernetesPage.tsx.
const STEPS: TourStep[] = [
  {
    id: 'cluster',
    selector: '[data-tour-id="cluster-picker"]',
    title: 'Pick a cluster',
    body: 'Every request runs with your own Entra ID permissions against the selected AKS cluster.',
  },
  {
    id: 'namespace',
    selector: '[data-tour-id="namespace-picker"]',
    title: 'Choose a project',
    body: 'Namespaces (projects) scope everything below it — pods, workloads, config, and more.',
  },
  {
    id: 'sidebar',
    selector: '[data-tour-id="sidebar-nav"]',
    title: 'Browse resources',
    body: 'Jump between Overview, Topology, Operations, and every Kubernetes kind from this navigation. Hover a kind to pin it to the top.',
  },
  {
    id: 'palette',
    selector: '[data-tour-id="command-palette-trigger"]',
    title: 'Command palette',
    body: 'Press ⌘K (or Ctrl+K) any time to jump straight to a resource, action, or view.',
  },
  {
    id: 'nora',
    selector: '[data-tour-id="nora-dock-toggle"]',
    title: 'Ask Nora',
    body: 'Nora is your AI copilot for this cluster — ask it to explain, diagnose, or fix what you are looking at.',
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function measure(selector: string): Rect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (!rect.width && !rect.height) return null;
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * First-run guided tour: a fixed spotlight overlay + callout, hand-rolled
 * with getBoundingClientRect() (no new dependency). Shown once, driven by
 * the server-side `tourCompleted` preference so it doesn't reappear across
 * browsers/machines for the same user.
 */
export function GuidedTour({ api }: { api: KubernetesConsoleApi }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    api
      .getPreferences()
      .then(prefs => {
        if (!cancelled && !prefs.tourCompleted) {
          setOpen(true);
          setStep(0);
        }
      })
      .catch(() => {
        /* the tour is best effort */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const finish = useCallback(() => {
    setOpen(false);
    api.setTourCompleted(true).catch(() => {
      /* best effort */
    });
  }, [api]);

  const recompute = useCallback(() => {
    const target = STEPS[step];
    if (!target) return;
    const found = measure(target.selector);
    if (found) {
      attemptsRef.current = 0;
      setRect(found);
      return;
    }
    // Elements can mount asynchronously (clusters/namespaces still loading)
    // — retry briefly before giving up on this step.
    if (attemptsRef.current < 15) {
      attemptsRef.current += 1;
      window.setTimeout(recompute, 200);
      return;
    }
    setRect(null);
    // The target never mounted (e.g. hidden by RBAC) — skip past it rather
    // than showing a callout with nothing to point at.
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else finish();
  }, [step, finish]);

  useEffect(() => {
    if (!open) return;
    attemptsRef.current = 0;
    recompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = () => recompute();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  if (!open || !rect) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const padding = 6;
  const spotlight = {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
  const calloutTop = Math.min(
    spotlight.top + spotlight.height + 12,
    window.innerHeight - 200,
  );
  const calloutLeft = Math.min(
    Math.max(spotlight.left, 16),
    window.innerWidth - 340,
  );

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
      <Box
        sx={{
          position: 'fixed',
          top: spotlight.top,
          left: spotlight.left,
          width: spotlight.width,
          height: spotlight.height,
          borderRadius: 1.5,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
          border: `2px solid ${COLORS.blue}`,
          pointerEvents: 'none',
          transition: 'all 200ms ease',
        }}
      />
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: calloutTop,
          left: calloutLeft,
          width: 320,
          p: 2,
          bgcolor: '#1F1F1F',
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            sx={{ color: COLORS.text, fontSize: 14, fontWeight: 700, flex: 1 }}
          >
            {current.title}
          </Typography>
          <IconButton
            size="small"
            onClick={finish}
            sx={{ color: COLORS.muted, mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography sx={{ color: COLORS.muted, fontSize: 12.5, mb: 2 }}>
          {current.body}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ color: COLORS.muted, fontSize: 11, flex: 1 }}>
            {step + 1} of {STEPS.length}
          </Typography>
          {step > 0 && (
            <Button
              size="small"
              onClick={() => setStep(s => s - 1)}
              sx={{ textTransform: 'none', mr: 1 }}
            >
              Back
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={() => (isLast ? finish() : setStep(s => s + 1))}
            sx={{ textTransform: 'none' }}
          >
            {isLast ? 'Done' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
